import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiFilter, FiHeart, FiSearch, FiX } from "react-icons/fi";
import "../css/shop.scss";
import productData, { categories } from "../Datas/productData";
import { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "All";
  const [category, setCategory] = useState(categories.includes(initialCategory) ? initialCategory : "All");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState("featured");
  const [materials, setMaterials] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(180);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [failedImages, setFailedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);

  const availableMaterials = useMemo(
    () => [...new Set(productData.map((product) => product.attributes.material))],
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setIsLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const categoryFromUrl = searchParams.get("category") || "All";
    const nextCategory = categories.includes(categoryFromUrl)
      ? categoryFromUrl
      : "All";
    const nextQuery = searchParams.get("q") || "";

    setCategory((current) =>
      current === nextCategory ? current : nextCategory
    );
    setQuery((current) => (current === nextQuery ? current : nextQuery));
  }, [searchParams]);

  useEffect(() => {
    const params = {};
    if (category !== "All") params.category = category;
    if (query.trim()) params.q = query.trim();
    setSearchParams(params, { replace: true });
  }, [category, query, setSearchParams]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const items = productData.filter((product) => {
      const searchable = [
        product.name,
        product.category,
        product.material,
        ...product.tags,
      ].join(" ").toLowerCase();
      return (
        (category === "All" || product.category === category) &&
        (!normalizedQuery || searchable.includes(normalizedQuery)) &&
        (!materials.length || materials.includes(product.attributes.material)) &&
        (!inStockOnly || product.stock > 0) &&
        product.price <= maxPrice
      );
    });

    return [...items].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "new") return (b.badge === "New") - (a.badge === "New") || b.id - a.id;
      return Number(Boolean(b.featured)) - Number(Boolean(a.featured)) || a.id - b.id;
    });
  }, [category, inStockOnly, materials, maxPrice, query, sort]);

  const toggleMaterial = (material) => {
    setMaterials((current) =>
      current.includes(material)
        ? current.filter((item) => item !== material)
        : [...current, material]
    );
  };

  const clearFilters = () => {
    setCategory("All");
    setQuery("");
    setMaterials([]);
    setInStockOnly(false);
    setMaxPrice(180);
    setSort("featured");
  };

  const addProduct = (product) => {
    addToCart(product.id, 1, product.size[0], product.colors[0]);
    setAddedId(product.id);
    window.dispatchEvent(new CustomEvent("ottireluna:cart-open"));
    window.setTimeout(() => setAddedId(null), 1500);
  };

  const hasFilters = category !== "All" || query || materials.length || inStockOnly || maxPrice < 180;

  return (
    <main className="shop-page" id="main-content">
      <header className="shop-hero">
        <div className="shop-hero__copy">
          <nav aria-label="Breadcrumb"><Link to="/">Home</Link><span>/</span><span>Shop</span></nav>
          <p className="eyebrow">The complete collection</p>
          <h1>Shop</h1>
          <p>
            Thoughtful objects selected for flowers, tables, gifts and everyday rituals.
          </p>
        </div>
        <div className="shop-hero__image">
          <img
            src="/main/ottireluna/linen-table.jpg"
            alt="Home accessories arranged on a bright dining table"
          />
        </div>
      </header>

      <nav className="shop-categories" aria-label="Product categories">
        {categories.map((item) => (
          <button
            key={item}
            type="button"
            className={category === item ? "is-active" : ""}
            onClick={() => setCategory(item)}
          >
            {item}
          </button>
        ))}
      </nav>

      <div className="shop-toolbar">
        <label className="shop-search">
          <FiSearch aria-hidden="true" />
          <span className="sr-only">Search products</span>
          <input
            type="search"
            value={query}
            placeholder="Search the collection"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
              <FiX aria-hidden="true" />
            </button>
          )}
        </label>
        <div className="shop-toolbar__actions">
          <button className="shop-filter-toggle" type="button" onClick={() => setFiltersOpen(true)}>
            <FiFilter aria-hidden="true" /> Filters
            {(materials.length > 0 || inStockOnly || maxPrice < 80) && (
              <span>{materials.length + Number(inStockOnly) + Number(maxPrice < 80)}</span>
            )}
          </button>
          <label className="shop-sort">
            <span>Sort by</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="featured">Featured</option>
              <option value="new">Newest</option>
              <option value="price-low">Price, low to high</option>
              <option value="price-high">Price, high to low</option>
              <option value="name">Name, A–Z</option>
            </select>
          </label>
        </div>
      </div>

      <div className="shop-layout">
        <div
          className={`shop-filter-overlay ${filtersOpen ? "is-open" : ""}`}
          onClick={() => setFiltersOpen(false)}
          aria-hidden="true"
        />
        <aside className={`shop-filters ${filtersOpen ? "is-open" : ""}`} aria-label="Product filters">
          <div className="shop-filters__mobile-head">
            <strong>Filter products</strong>
            <button type="button" aria-label="Close filters" onClick={() => setFiltersOpen(false)}>
              <FiX aria-hidden="true" />
            </button>
          </div>
          <fieldset>
            <legend>Material</legend>
            {availableMaterials.map((material) => (
              <label key={material}>
                <input
                  type="checkbox"
                  checked={materials.includes(material)}
                  onChange={() => toggleMaterial(material)}
                />
                <span>{material}</span>
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Price</legend>
            <div className="shop-price-label"><span>$0</span><span>{formatPrice(maxPrice)}</span></div>
            <input
              className="shop-range"
              type="range"
              min="10"
              max="180"
              step="1"
              value={maxPrice}
              onChange={(event) => setMaxPrice(Number(event.target.value))}
              aria-label="Maximum price"
            />
          </fieldset>
          <fieldset>
            <legend>Availability</legend>
            <label>
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={(event) => setInStockOnly(event.target.checked)}
              />
              <span>In stock only</span>
            </label>
          </fieldset>
          <button className="shop-clear" type="button" onClick={clearFilters} disabled={!hasFilters}>
            Clear all
          </button>
          <button className="button button--dark shop-apply" type="button" onClick={() => setFiltersOpen(false)}>
            Show {filteredProducts.length} products
          </button>
        </aside>

        <section className="shop-results" aria-live="polite" aria-busy={isLoading}>
          <div className="shop-results__count">
            <span>{isLoading ? "Loading collection…" : `${filteredProducts.length} products`}</span>
            {hasFilters && <button type="button" onClick={clearFilters}>Clear filters</button>}
          </div>

          {isLoading ? (
            <div className="shop-grid shop-grid--skeleton" aria-label="Loading products">
              {Array.from({ length: 8 }).map((_, index) => (
                <div className="shop-skeleton" key={index}><span /><i /><i /></div>
              ))}
            </div>
          ) : filteredProducts.length ? (
            <div className="shop-grid">
              {filteredProducts.map((product) => (
                <article className="shop-card" key={product.id}>
                  <div className={`shop-card__media ${failedImages.includes(product.id) ? "is-error" : ""}`}>
                    {product.badge && <span className="shop-card__badge">{product.badge}</span>}
                    <button
                      className={`shop-card__wish ${isWishlisted(product.id) ? "is-active" : ""}`}
                      type="button"
                      aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                      aria-pressed={isWishlisted(product.id)}
                      onClick={() => toggleWishlist(product.id)}
                    >
                      <FiHeart aria-hidden="true" />
                    </button>
                    <Link to={`/shopDetails/${product.id}`}>
                      {failedImages.includes(product.id) ? (
                        <span className="shop-card__error">Image unavailable</span>
                      ) : (
                        <img
                          src={product.image}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          onError={() => setFailedImages((current) => [...new Set([...current, product.id])])}
                        />
                      )}
                    </Link>
                  </div>
                  <div className="shop-card__info">
                    <Link to={`/shopDetails/${product.id}`}><h2>{product.name}</h2></Link>
                    <p>{product.category}</p>
                    <span>{formatPrice(product.price)}</span>
                  </div>
                  <button className="shop-card__add" type="button" onClick={() => addProduct(product)} disabled={!product.stock}>
                    {product.stock ? (addedId === product.id ? "Added to cart" : "Add to cart") : "Sold out"}
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <span aria-hidden="true">☀</span>
              <h2>No objects found</h2>
              <p>Try a broader search or clear the current filters.</p>
              <button className="button" type="button" onClick={clearFilters}>Reset collection</button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default Shop;
